import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { getApiBaseUrl, resolveUploadedMediaUrl } from "../../config/api";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import {
    fetchTeacherCurrentTermResources,
    recordResourceDownload,
    updateTeachingResourceMeta,
} from "../../utils/termResourceApi";

const TYPE_OPTIONS = ["All", "PastPaper", "Notes", "Book", "TLM"];
const TYPE_LABELS = {
    PastPaper: "Past paper",
    Notes: "Notes",
    Book: "Book",
    TLM: "TLM",
};
const TYPE_CHIP = {
    PastPaper: "primary",
    Notes: "info",
    Book: "secondary",
    TLM: "warning",
};

function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
    return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

const emptyForm = () => ({
    title: "",
    description: "",
    resourceType: "PastPaper",
    visibility: "Class",
    isShared: true,
});

const TeacherResources = () => {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [period, setPeriod] = useState(null);
    const [typeFilter, setTypeFilter] = useState("All");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const loadResources = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const data = await fetchTeacherCurrentTermResources(teacherId, {
                resourceType: typeFilter !== "All" ? typeFilter : undefined,
            });
            setPeriod(data?.period || null);
            setRows(Array.isArray(data?.resources) ? data.resources : []);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load resources");
            setShowPopup(true);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [teacherId, typeFilter]);

    useEffect(() => {
        loadResources();
    }, [loadResources]);

    const filteredRows = useMemo(() => rows, [rows]);

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const openUpload = () => {
        setForm(emptyForm());
        setFile(null);
        setDialogOpen(true);
    };

    const handleUpload = async () => {
        if (!teacherId || !schoolId || !subjectId || !classId) {
            setMessage("Missing teacher, school, class or subject information.");
            setShowPopup(true);
            return;
        }
        if (!form.title.trim()) {
            setMessage("Title is required.");
            setShowPopup(true);
            return;
        }
        if (!file) {
            setMessage("Please choose a file to upload.");
            setShowPopup(true);
            return;
        }

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("teacherId", teacherId);
            fd.append("schoolId", schoolId);
            fd.append("subjectId", subjectId);
            fd.append("classId", classId);
            fd.append("title", form.title.trim());
            fd.append("description", form.description.trim());
            fd.append("resourceType", form.resourceType);
            fd.append("visibility", form.visibility);
            fd.append("isShared", form.isShared ? "true" : "false");

            const { data } = await axios.post(`${getApiBaseUrl()}/TeachingResource`, fd);

            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setShowPopup(true);
            } else {
                setMessage("Resource uploaded successfully");
                setShowPopup(true);
                setDialogOpen(false);
                loadResources();
            }
        } catch (err) {
            const data = err.response?.data;
            let msg = "Upload failed";
            if (typeof data === "object" && data?.message) {
                msg = data.message;
            } else if (typeof data === "string" && data.trim()) {
                msg = data;
            } else if (err.message) {
                msg = err.message;
            }
            setMessage(msg);
            setShowPopup(true);
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (row) => {
        const id = row.id || row._id;
        const downloadUrl = resolveUploadedMediaUrl(row.fileUrl);
        try {
            await recordResourceDownload(id);
            loadResources();
        } catch {
            /* still allow download */
        }
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
    };

    const toggleShared = async (row) => {
        const id = row.id || row._id;
        try {
            await updateTeachingResourceMeta(id, { isShared: !row.isShared });
            loadResources();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not update sharing");
            setShowPopup(true);
        }
    };

    const periodLabel = period
        ? [period.term, period.academicYear].filter(Boolean).join(" · ")
        : "Current term";

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this resource? Students will no longer be able to download it.")) return;
        try {
            const { data } = await axios.delete(`${getApiBaseUrl()}/TeachingResource/${id}`);
            if (data?.message === "Resource deleted") {
                setMessage("Resource deleted");
                setShowPopup(true);
                loadResources();
            } else {
                setMessage(data?.message || "Could not delete");
                setShowPopup(true);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not delete");
            setShowPopup(true);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            Learning materials
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Teaching resources
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Upload past papers, notes, TLMs and books for {className} · {subjectName}
                        </Typography>
                        <Chip size="small" label={periodLabel} color="primary" variant="outlined" sx={{ mt: 1 }} />
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadOutlinedIcon />}
                        onClick={openUpload}
                        sx={{ fontWeight: 700, alignSelf: { xs: "stretch", sm: "center" } }}
                    >
                        Upload resource
                    </Button>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2.5,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Resource type</InputLabel>
                        <Select
                            label="Resource type"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            {TYPE_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t === "All" ? "All types" : TYPE_LABELS[t] || t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Paper>

                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 2.5,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Title</StyledTableCell>
                                <StyledTableCell>Type</StyledTableCell>
                                <StyledTableCell>Term / Year</StyledTableCell>
                                <StyledTableCell>File</StyledTableCell>
                                <StyledTableCell>Downloads</StyledTableCell>
                                <StyledTableCell>Shared</StyledTableCell>
                                <StyledTableCell>Uploaded</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell colSpan={8}>
                                            <Skeleton height={36} />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            ) : filteredRows.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <InsertDriveFileOutlinedIcon
                                            sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                                        />
                                        <Typography color="text.secondary">
                                            No resources yet. Upload past papers, class notes, or textbooks for your
                                            students.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                filteredRows.map((row) => {
                                    const id = row.id || row._id;
                                    const type = row.resourceType;
                                    return (
                                        <StyledTableRow key={id}>
                                            <StyledTableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {row.title}
                                                </Typography>
                                                {row.description ? (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {row.description}
                                                    </Typography>
                                                ) : null}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    size="small"
                                                    label={TYPE_LABELS[type] || type}
                                                    color={TYPE_CHIP[type] || "default"}
                                                    variant="outlined"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {[row.term, row.academicYear].filter(Boolean).join(" · ") || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Typography variant="caption" display="block" noWrap sx={{ maxWidth: 160 }}>
                                                    {row.fileName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatBytes(row.fileSize)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell>{row.downloadCount ?? 0}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.isShared !== false ? "Yes" : "No"}
                                                    color={row.isShared !== false ? "success" : "default"}
                                                    variant="outlined"
                                                    onClick={() => toggleShared(row)}
                                                    sx={{ cursor: "pointer" }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>{formatDate(row.createdAt)}</StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Tooltip title="Download">
                                                    <IconButton size="small" onClick={() => handleDownload(row)}>
                                                        <DownloadOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            <Dialog open={dialogOpen} onClose={() => !uploading && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload resource</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Resource type</InputLabel>
                            <Select
                                label="Resource type"
                                value={form.resourceType}
                                onChange={(e) => setField("resourceType", e.target.value)}
                            >
                                <MenuItem value="PastPaper">Past paper</MenuItem>
                                <MenuItem value="Notes">Notes</MenuItem>
                                <MenuItem value="Book">Book</MenuItem>
                                <MenuItem value="TLM">TLM / teaching aid</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Visibility</InputLabel>
                            <Select
                                label="Visibility"
                                value={form.visibility}
                                onChange={(e) => setField("visibility", e.target.value)}
                            >
                                <MenuItem value="Class">Class only</MenuItem>
                                <MenuItem value="School">Whole school (staff)</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            required
                            label="Title"
                            value={form.title}
                            onChange={(e) => setField("title", e.target.value)}
                            placeholder="e.g. 2023 MSCE Mathematics Paper 1"
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Description (optional)"
                            value={form.description}
                            onChange={(e) => setField("description", e.target.value)}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Resources are tagged to {periodLabel} automatically.
                        </Typography>
                        <Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.zip,.rar,.epub,.png,.jpg,.jpeg,.gif,.webp"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<CloudUploadOutlinedIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                fullWidth
                            >
                                {file ? file.name : "Choose file (max 25 MB)"}
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                PDF, Word, PowerPoint, images, ZIP, EPUB, or TXT
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleUpload} disabled={uploading}>
                        {uploading ? "Uploading…" : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Box>
    );
};

export default TeacherResources;
